import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  query,
  where,
  orderBy,
  onSnapshot,
  writeBatch,
  arrayUnion,
  arrayRemove,
  collectionGroup,
  addDoc,
  limit
} from 'firebase/firestore';
import { db, appId } from '../../../services/firebase';
import { getUserWorkspace } from '../../../services/firestore';
import { createNotification } from '../../../contexts/NotificationContext';
import { trackRead } from '../../../services/firestoreMonitor';

// Helper: notify assigned users
const notifyAssignees = async (assigneeEmails, senderUserId, notification) => {
  if (!assigneeEmails || assigneeEmails.length === 0) return;
  try {
    const usersRef = collection(db, 'users');
    const snapshot = await getDocs(usersRef);
    const allUsers = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    for (const email of assigneeEmails) {
      const targetUser = allUsers.find(u => u.email === email);
      if (targetUser && targetUser.id !== senderUserId) {
        await createNotification(targetUser.id, notification);
      }
    }
  } catch (error) {
    console.error('Error notifying assignees:', error);
  }
};

const TASKS_BASE = `artifacts/${appId}/users`;

// ==================== PROYECTOS ====================

export const getProjectsForUser = async (userId) => {
  try {
    // Obtener el workspace del usuario (puede ser su propio ID o el del admin si es colaborador)
    const workspaceId = await getUserWorkspace(userId);
    const projectsRef = collection(db, `${TASKS_BASE}/${workspaceId}/projects`);
    const snapshot = await getDocs(projectsRef);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error al obtener proyectos:', error);
    return [];
  }
};

export const createProject = async (userId, projectData) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    const projectId = `project-${Date.now()}`;
    const projectRef = doc(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}`);

    await setDoc(projectRef, {
      id: projectId,
      ...projectData,
      creadoPor: userId, // Guardar quién creó el proyecto
      workspaceId: workspaceId, // Guardar a qué workspace pertenece
      miembros: [userId],
      fechaCreacion: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    return projectId;
  } catch (error) {
    console.error('Error al crear proyecto:', error);
    throw error;
  }
};

export const updateProject = async (userId, projectId, updates) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    const projectRef = doc(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}`);
    await updateDoc(projectRef, {
      ...updates,
      lastModifiedBy: userId, // Guardar quién modificó
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error al actualizar proyecto:', error);
    throw error;
  }
};

export const deleteProject = async (userId, projectId) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    const projectRef = doc(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}`);
    await deleteDoc(projectRef);
  } catch (error) {
    console.error('Error al eliminar proyecto:', error);
    throw error;
  }
};

// ==================== PLANTILLAS ====================

export const saveAsTemplate = async (userId, projectId) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    // Get project data
    const projectRef = doc(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}`);
    const projectSnap = await getDoc(projectRef);
    if (!projectSnap.exists()) throw new Error('Proyecto no encontrado');
    const projectData = projectSnap.data();

    // Get folders
    const foldersSnap = await getDocs(collection(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders`));
    const folders = [];
    for (const folderDoc of foldersSnap.docs) {
      const folderData = folderDoc.data();
      // Get lists for each folder
      const listsSnap = await getDocs(collection(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderDoc.id}/lists`));
      const lists = [];
      for (const listDoc of listsSnap.docs) {
        const listData = listDoc.data();
        // Get sections for each list
        const sectionsSnap = await getDocs(collection(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderDoc.id}/lists/${listDoc.id}/sections`));
        const sections = sectionsSnap.docs.map(s => ({ ...s.data() }));
        lists.push({ ...listData, sections });
      }
      folders.push({ ...folderData, lists });
    }

    // Save template
    const templateId = `template-${Date.now()}`;
    const templateRef = doc(db, `${TASKS_BASE}/${workspaceId}/templates/${templateId}`);
    await setDoc(templateRef, {
      id: templateId,
      name: projectData.nombre || projectData.name || 'Plantilla',
      description: projectData.descripcion || '',
      icon: projectData.icon || null,
      color: projectData.color || null,
      folders,
      createdBy: userId,
      createdAt: serverTimestamp(),
    });
    return templateId;
  } catch (error) {
    console.error('Error saving template:', error);
    throw error;
  }
};

export const getTemplates = async (userId) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    const snapshot = await getDocs(collection(db, `${TASKS_BASE}/${workspaceId}/templates`));
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error('Error getting templates:', error);
    return [];
  }
};

export const deleteTemplate = async (userId, templateId) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    await deleteDoc(doc(db, `${TASKS_BASE}/${workspaceId}/templates/${templateId}`));
  } catch (error) {
    console.error('Error deleting template:', error);
    throw error;
  }
};

export const createProjectFromTemplate = async (userId, templateId, projectName) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    const templateRef = doc(db, `${TASKS_BASE}/${workspaceId}/templates/${templateId}`);
    const templateSnap = await getDoc(templateRef);
    if (!templateSnap.exists()) throw new Error('Plantilla no encontrada');
    const template = templateSnap.data();

    // Create project
    const projectId = `project-${Date.now()}`;
    const projectRef = doc(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}`);
    await setDoc(projectRef, {
      id: projectId,
      nombre: projectName || template.name,
      descripcion: template.description || '',
      icon: template.icon || null,
      color: template.color || null,
      creadoPor: userId,
      workspaceId,
      miembros: [userId],
      fromTemplate: templateId,
      fechaCreacion: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    // Recreate folders and lists
    for (const folder of (template.folders || [])) {
      const folderId = `folder-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      const folderRef = doc(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}`);
      await setDoc(folderRef, {
        id: folderId,
        nombre: folder.nombre || folder.name,
        projectId,
        updatedAt: serverTimestamp(),
      });

      for (const list of (folder.lists || [])) {
        const listId = `list-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
        const listRef = doc(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}/lists/${listId}`);
        await setDoc(listRef, {
          id: listId,
          nombre: list.nombre || list.name,
          folderId,
          projectId,
          customColumns: list.customColumns || null,
          updatedAt: serverTimestamp(),
        });

        // Create sections
        for (const section of (list.sections || [])) {
          const sectionId = `section-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
          const sectionRef = doc(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}/lists/${listId}/sections/${sectionId}`);
          await setDoc(sectionRef, {
            id: sectionId,
            nombre: section.nombre,
            color: section.color,
            orden: section.orden || 0,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
        }
      }
    }

    return projectId;
  } catch (error) {
    console.error('Error creating project from template:', error);
    throw error;
  }
};

// ==================== CARPETAS ====================

export const getFoldersForProject = async (userId, projectId) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    const foldersRef = collection(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders`);
    const snapshot = await getDocs(query(foldersRef, orderBy('orden', 'asc')));
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error al obtener carpetas:', error);
    return [];
  }
};

export const createFolder = async (userId, projectId, folderData) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    const folderId = `folder-${Date.now()}`;
    const folderRef = doc(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}`);

    await setDoc(folderRef, {
      id: folderId,
      projectId,
      ...folderData,
      orden: Date.now(),
      updatedAt: serverTimestamp()
    });

    return folderId;
  } catch (error) {
    console.error('Error al crear carpeta:', error);
    throw error;
  }
};

export const updateFolder = async (userId, projectId, folderId, updates) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    const folderRef = doc(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}`);
    await updateDoc(folderRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error al actualizar carpeta:', error);
    throw error;
  }
};

export const deleteFolder = async (userId, projectId, folderId) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    const folderRef = doc(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}`);
    await deleteDoc(folderRef);
  } catch (error) {
    console.error('Error al eliminar carpeta:', error);
    throw error;
  }
};

export const duplicateFolderWithLists = async (userId, projectId, sourceFolderId, targetProjectId = projectId) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    const sourceRef = doc(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${sourceFolderId}`);
    const sourceFolderSnap = await getDoc(sourceRef);

    if (!sourceFolderSnap.exists()) {
      throw new Error('Carpeta no encontrada');
    }

    const sourceData = sourceFolderSnap.data();
    const newFolderId = `folder-${Date.now()}`;
    const newFolderRef = doc(db, `${TASKS_BASE}/${workspaceId}/projects/${targetProjectId}/folders/${newFolderId}`);

    await setDoc(newFolderRef, {
      ...sourceData,
      id: newFolderId,
      nombre: `${sourceData.nombre} (copia)`,
      projectId: targetProjectId,
      fechaCreacion: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    return newFolderId;
  } catch (error) {
    console.error('Error al duplicar carpeta:', error);
    throw error;
  }
};

// ==================== LISTAS ====================

export const getListsForFolder = async (userId, projectId, folderId) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    const listsRef = collection(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}/lists`);
    const snapshot = await getDocs(listsRef);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error al obtener listas:', error);
    return [];
  }
};

export const getListDetails = async (userId, listId) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    // This is tricky because we don't know the project or folder ID.
    // We have to query a collection group 'lists' to find the document.
    const listsGroup = collectionGroup(db, 'lists');
    const q = query(listsGroup, where('id', '==', listId));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      console.error('Lista no encontrada');
      return null;
    }

    const doc = querySnapshot.docs[0];
    return { id: doc.id, ...doc.data() };
  } catch (error) {
    console.error('Error al obtener detalles de la lista:', error);
    return null;
  }
};

export const createList = async (userId, projectId, folderId, listData) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    const listId = `list-${Date.now()}`;
    const listRef = doc(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}/lists/${listId}`);

    await setDoc(listRef, {
      id: listId,
      folderId,
      projectId,
      ...listData,
      updatedAt: serverTimestamp()
    });

    return listId;
  } catch (error) {
    console.error('Error al crear lista:', error);
    throw error;
  }
};

export const updateList = async (userId, projectId, folderId, listId, updates) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    const listRef = doc(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}/lists/${listId}`);
    await updateDoc(listRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error al actualizar lista:', error);
    throw error;
  }
};

export const deleteList = async (userId, projectId, folderId, listId) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    const listRef = doc(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}/lists/${listId}`);
    await deleteDoc(listRef);
  } catch (error) {
    console.error('Error al eliminar lista:', error);
    throw error;
  }
};

export const duplicateList = async (userId, projectId, sourceFolderId, listId, targetFolderId) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    // Obtener datos de la lista original
    const sourceListRef = doc(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${sourceFolderId}/lists/${listId}`);
    const sourceListSnap = await getDoc(sourceListRef);

    if (!sourceListSnap.exists()) {
      throw new Error('Lista no encontrada');
    }

    const sourceData = sourceListSnap.data();

    // Crear nueva lista con datos copiados
    const newListId = `list-${Date.now()}`;
    const newListRef = doc(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${targetFolderId}/lists/${newListId}`);

    await setDoc(newListRef, {
      ...sourceData,
      id: newListId,
      nombre: `${sourceData.nombre} (copia)`,
      folderId: targetFolderId,
      fechaCreacion: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    return newListId;
  } catch (error) {
    console.error('Error al duplicar lista:', error);
    throw error;
  }
};

// ==================== HELPERS PARA allAsignados ====================

// Recolecta recursivamente todos los emails de asignados (tarea + subtareas)
const collectAllAsignados = (item) => {
  const emails = new Set(item.asignados || []);
  if (item.subtareas) {
    for (const sub of item.subtareas) {
      for (const email of collectAllAsignados(sub)) {
        emails.add(email);
      }
    }
  }
  return emails;
};

// Recalcula y guarda allAsignados en el documento de la tarea
const recalculateAllAsignados = async (taskRef) => {
  const snap = await getDoc(taskRef);
  if (!snap.exists()) return;
  const task = snap.data();
  const allAsignados = [...collectAllAsignados(task)];
  await updateDoc(taskRef, { allAsignados });
};

// Migración: recorre TODAS las tareas del workspace y agrega allAsignados si falta
export const migrateAllAsignados = async (userId) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    const projectsRef = collection(db, `${TASKS_BASE}/${workspaceId}/projects`);
    const projectsSnap = await getDocs(projectsRef);
    let migrated = 0;

    for (const projectDoc of projectsSnap.docs) {
      const foldersSnap = await getDocs(collection(projectDoc.ref, 'folders'));
      for (const folderDoc of foldersSnap.docs) {
        const listsSnap = await getDocs(collection(folderDoc.ref, 'lists'));
        for (const listDoc of listsSnap.docs) {
          const itemsSnap = await getDocs(collection(listDoc.ref, 'items'));
          for (const itemDoc of itemsSnap.docs) {
            const task = itemDoc.data();
            // Solo migrar si no tiene allAsignados o si tiene subtareas que podrían tener asignados
            if (!task.allAsignados || (task.subtareas && task.subtareas.length > 0 && !task._allAsignadosMigrated)) {
              const allAsignados = [...collectAllAsignados(task)];
              if (allAsignados.length > 0) {
                await updateDoc(itemDoc.ref, { allAsignados });
                migrated++;
              }
            }
          }
        }
      }
    }

    console.log(`Migración allAsignados completada: ${migrated} tareas actualizadas`);
    return migrated;
  } catch (error) {
    console.error('Error en migración allAsignados:', error);
    return 0;
  }
};

// Migración: recorre TODAS las tareas del workspace y agrega workspaceId si falta
// Necesario para que las queries collectionGroup filtren por workspace (optimización lecturas)
export const migrateWorkspaceId = async (userId) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    const projectsRef = collection(db, `${TASKS_BASE}/${workspaceId}/projects`);
    const projectsSnap = await getDocs(projectsRef);
    let migrated = 0;
    const batch = [];

    for (const projectDoc of projectsSnap.docs) {
      const foldersSnap = await getDocs(collection(projectDoc.ref, 'folders'));
      for (const folderDoc of foldersSnap.docs) {
        const listsSnap = await getDocs(collection(folderDoc.ref, 'lists'));
        for (const listDoc of listsSnap.docs) {
          const itemsSnap = await getDocs(collection(listDoc.ref, 'items'));
          for (const itemDoc of itemsSnap.docs) {
            const task = itemDoc.data();
            if (!task.workspaceId) {
              batch.push(updateDoc(itemDoc.ref, { workspaceId }));
              migrated++;
              // Ejecutar en lotes de 50 para no saturar
              if (batch.length >= 50) {
                await Promise.all(batch);
                batch.length = 0;
              }
            }
          }
        }
      }
    }

    // Ejecutar lote restante
    if (batch.length > 0) {
      await Promise.all(batch);
    }

    console.log(`Migración workspaceId completada: ${migrated} tareas actualizadas`);
    return migrated;
  } catch (error) {
    console.error('Error en migración workspaceId:', error);
    return 0;
  }
};

// ==================== TAREAS ====================

export const getTasksForList = async (userId, projectId, folderId, listId) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    const tasksRef = collection(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}/lists/${listId}/items`);
    const snapshot = await getDocs(query(tasksRef, orderBy('fechaCreacion', 'desc')));
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error al obtener tareas:', error);
    return [];
  }
};

export const createTask = async (userId, projectId, folderId, listId, taskData) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    const taskId = `task-${Date.now()}`;
    const taskRef = doc(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}/lists/${listId}/items/${taskId}`);

    const asignados = taskData.asignados || [];
    await setDoc(taskRef, {
      id: taskId,
      listId,
      folderId,
      projectId,
      workspaceId,
      ...taskData,
      seccionId: taskData.seccionId || null,
      estado: 'planificacion',
      prioridad: taskData.prioridad || 'media',
      asignados,
      allAsignados: asignados, // Para búsqueda cross-project incluyendo subtareas
      etiquetas: taskData.etiquetas || [],
      fechaVencimiento: taskData.fechaVencimiento || null,
      subtareas: [],
      comentarios: [],
      adjuntos: [],
      creadoPor: userId,
      fechaCreacion: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    return taskId;
  } catch (error) {
    console.error('Error al crear tarea:', error);
    throw error;
  }
};

export const duplicateTask = async (userId, projectId, folderId, listId, sourceTask) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    const taskId = `task-${Date.now()}`;
    const taskRef = doc(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}/lists/${listId}/items/${taskId}`);

    // Deep clone subtareas with new IDs
    const cloneSubtareas = (subtareas) => {
      if (!subtareas || subtareas.length === 0) return [];
      return subtareas.map(sub => ({
        ...sub,
        id: `subtask-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        completado: false,
        estado: 'planificacion',
        subtareas: cloneSubtareas(sub.subtareas),
      }));
    };

    await setDoc(taskRef, {
      id: taskId,
      listId, folderId, projectId,
      workspaceId,
      titulo: `${sourceTask.titulo} (Copia)`,
      descripcion: sourceTask.descripcion || '',
      seccionId: sourceTask.seccionId || null,
      estado: 'planificacion',
      prioridad: sourceTask.prioridad || 'media',
      asignados: sourceTask.asignados || [],
      allAsignados: sourceTask.allAsignados || sourceTask.asignados || [],
      etiquetas: sourceTask.etiquetas || [],
      fechaVencimiento: sourceTask.fechaVencimiento || null,
      recurrence: sourceTask.recurrence || null,
      subtareas: cloneSubtareas(sourceTask.subtareas),
      comentarios: [],
      adjuntos: [],
      creadoPor: userId,
      fechaCreacion: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    return taskId;
  } catch (error) {
    console.error('Error al duplicar tarea:', error);
    throw error;
  }
};

// Helper: calculate next recurrence date
const getNextRecurrenceDate = (currentDate, recurrence) => {
  const base = currentDate ? new Date(typeof currentDate === 'object' && currentDate.seconds ? currentDate.seconds * 1000 : currentDate) : new Date();
  const next = new Date(base);
  switch (recurrence) {
    case 'daily': next.setDate(next.getDate() + 1); break;
    case 'weekdays':
      next.setDate(next.getDate() + 1);
      while (next.getDay() === 0 || next.getDay() === 6) next.setDate(next.getDate() + 1);
      break;
    case 'weekly': next.setDate(next.getDate() + 7); break;
    case 'biweekly': next.setDate(next.getDate() + 14); break;
    case 'monthly': next.setMonth(next.getMonth() + 1); break;
    case 'quarterly': next.setMonth(next.getMonth() + 3); break;
    default: return null;
  }
  return next.toISOString();
};

export const updateTask = async (userId, projectId, folderId, listId, taskId, updates) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    const taskRef = doc(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}/lists/${listId}/items/${taskId}`);

    // Build activity log entries for tracked fields
    const activityEntries = [];
    const labelMap = { estado: 'estado', prioridad: 'prioridad', asignados: 'asignados', fechaVencimiento: 'fecha de vencimiento', titulo: 'título', descripcion: 'descripción', recurrence: 'recurrencia' };
    const valueLabels = {
      planificacion: 'Planificación', 'en-curso': 'En Curso', finalizado: 'Finalizado',
      alta: 'Alta', media: 'Media', baja: 'Baja',
      daily: 'Diaria', weekdays: 'Días laborales', weekly: 'Semanal', biweekly: 'Quincenal', monthly: 'Mensual', quarterly: 'Trimestral',
    };
    for (const key of Object.keys(updates)) {
      if (labelMap[key]) {
        const val = updates[key];
        let displayVal = Array.isArray(val) ? val.join(', ') : (valueLabels[val] || val || 'vacío');
        activityEntries.push({
          id: `activity-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          usuarioId: userId,
          accion: 'actualizacion',
          campo: key,
          detalles: `Cambió ${labelMap[key]} a "${displayVal}"`,
          fecha: new Date().toISOString()
        });
      }
    }

    const updatePayload = { ...updates, updatedAt: serverTimestamp() };
    if (activityEntries.length > 0) {
      updatePayload.actividad = arrayUnion(...activityEntries);
    }
    // Backfill lazy: agregar workspaceId a tasks existentes que no lo tienen
    // Esto permite que las queries collectionGroup filtren por workspace
    updatePayload.workspaceId = workspaceId;
    await updateDoc(taskRef, updatePayload);

    // Recalcular allAsignados si cambiaron los asignados
    if (updates.asignados) {
      await recalculateAllAsignados(taskRef);
    }
    // Notify on assignment
    if (updates.asignados) {
      const taskSnap = await getDoc(taskRef);
      const title = taskSnap.data()?.titulo || 'Tarea';
      notifyAssignees(updates.asignados, userId, {
        type: 'assigned',
        title: 'Te asignaron una tarea',
        body: title,
      }).catch(() => {});
    }
    // Handle recurrence: create next task when completed
    if (updates.estado === 'finalizado') {
      const taskSnap = await getDoc(taskRef);
      const taskData = taskSnap.data();
      // Notify assignees about completion
      if (taskData?.asignados) {
        notifyAssignees(taskData.asignados, userId, {
          type: 'completed',
          title: 'Tarea completada',
          body: taskData.titulo || 'Tarea',
        }).catch(() => {});
      }
      if (taskData?.recurrence && taskData.recurrence !== 'none') {
        const nextDate = getNextRecurrenceDate(taskData.fechaVencimiento, taskData.recurrence);
        const newTaskId = `task-${Date.now()}`;
        const newTaskRef = doc(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}/lists/${listId}/items/${newTaskId}`);
        await setDoc(newTaskRef, {
          id: newTaskId,
          listId,
          folderId,
          projectId,
          workspaceId,
          titulo: taskData.titulo,
          descripcion: taskData.descripcion || '',
          seccionId: taskData.seccionId || null,
          estado: 'planificacion',
          prioridad: taskData.prioridad || 'media',
          asignados: taskData.asignados || [],
          allAsignados: taskData.allAsignados || [],
          etiquetas: taskData.etiquetas || [],
          fechaVencimiento: nextDate,
          recurrence: taskData.recurrence,
          subtareas: [],
          comentarios: [],
          adjuntos: [],
          creadoPor: userId,
          fechaCreacion: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }
    }
    // Notify on approval changes
    if (updates.approval) {
      const taskSnap2 = updates.estado === 'finalizado' ? null : await getDoc(taskRef);
      const taskTitle = taskSnap2?.data()?.titulo || 'Tarea';
      if (updates.approval.status === 'pending' && updates.approval.approverEmail) {
        notifyAssignees([updates.approval.approverEmail], userId, {
          type: 'assigned',
          title: 'Solicitud de aprobación',
          body: `Se solicita tu aprobación para: ${taskTitle}`,
        }).catch(() => {});
      } else if (updates.approval.status === 'approved' && updates.approval.requestedBy) {
        notifyAssignees([updates.approval.requestedBy], userId, {
          type: 'completed',
          title: 'Tarea aprobada',
          body: taskTitle,
        }).catch(() => {});
      } else if (updates.approval.status === 'rejected' && updates.approval.requestedBy) {
        notifyAssignees([updates.approval.requestedBy], userId, {
          type: 'error',
          title: 'Tarea rechazada',
          body: taskTitle,
        }).catch(() => {});
      }
    }
    // Execute automation rules
    executeRules(userId, projectId, folderId, listId, taskId, updates, null).catch(() => {});
  } catch (error) {
    console.error('Error al actualizar tarea:', error);
    throw error;
  }
};

export const deleteTask = async (userId, projectId, folderId, listId, taskId) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    const taskRef = doc(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}/lists/${listId}/items/${taskId}`);
    await deleteDoc(taskRef);
  } catch (error) {
    console.error('Error al eliminar tarea:', error);
    throw error;
  }
};

// ==================== BULK OPERATIONS ====================

export const bulkUpdateTasks = async (userId, projectId, folderId, listId, taskIds, updates) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    const batch = writeBatch(db);
    taskIds.forEach(taskId => {
      const taskRef = doc(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}/lists/${listId}/items/${taskId}`);
      batch.update(taskRef, { ...updates, updatedAt: serverTimestamp() });
    });
    await batch.commit();
  } catch (error) {
    console.error('Error en actualización masiva:', error);
    throw error;
  }
};

export const bulkDeleteTasks = async (userId, projectId, folderId, listId, taskIds) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    const batch = writeBatch(db);
    taskIds.forEach(taskId => {
      const taskRef = doc(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}/lists/${listId}/items/${taskId}`);
      batch.delete(taskRef);
    });
    await batch.commit();
  } catch (error) {
    console.error('Error en eliminación masiva:', error);
    throw error;
  }
};

// ==================== AUTOMATIZACIONES ====================

export const getAutomationRules = async (userId, projectId, folderId, listId) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    const rulesRef = collection(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}/lists/${listId}/rules`);
    const snapshot = await getDocs(rulesRef);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error('Error getting rules:', error);
    return [];
  }
};

export const subscribeToRules = (userId, projectId, folderId, listId, callback) => {
  let cancelled = false;
  let unsubscribe = null;
  getUserWorkspace(userId).then(workspaceId => {
    if (cancelled) return;
    const rulesRef = collection(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}/lists/${listId}/rules`);
    unsubscribe = onSnapshot(query(rulesRef, limit(20)), (snapshot) => {
      callback(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    });
  });
  return () => { cancelled = true; if (unsubscribe) unsubscribe(); };
};

export const createRule = async (userId, projectId, folderId, listId, ruleData) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    const ruleId = `rule-${Date.now()}`;
    const ruleRef = doc(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}/lists/${listId}/rules/${ruleId}`);
    await setDoc(ruleRef, {
      id: ruleId,
      ...ruleData,
      enabled: true,
      createdAt: serverTimestamp(),
    });
    return ruleId;
  } catch (error) {
    console.error('Error creating rule:', error);
    throw error;
  }
};

export const deleteRule = async (userId, projectId, folderId, listId, ruleId) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    const ruleRef = doc(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}/lists/${listId}/rules/${ruleId}`);
    await deleteDoc(ruleRef);
  } catch (error) {
    console.error('Error deleting rule:', error);
    throw error;
  }
};

export const toggleRule = async (userId, projectId, folderId, listId, ruleId, enabled) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    const ruleRef = doc(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}/lists/${listId}/rules/${ruleId}`);
    await updateDoc(ruleRef, { enabled });
  } catch (error) {
    console.error('Error toggling rule:', error);
    throw error;
  }
};

// Execute automation rules for a task update
export const executeRules = async (userId, projectId, folderId, listId, taskId, updates, taskData) => {
  try {
    const rules = await getAutomationRules(userId, projectId, folderId, listId);
    const enabledRules = rules.filter(r => r.enabled);

    for (const rule of enabledRules) {
      let shouldExecute = false;

      // Check trigger condition
      switch (rule.trigger) {
        case 'status_change':
          if (updates.estado && updates.estado === rule.triggerValue) shouldExecute = true;
          break;
        case 'priority_change':
          if (updates.prioridad && updates.prioridad === rule.triggerValue) shouldExecute = true;
          break;
        case 'assigned':
          if (updates.asignados) shouldExecute = true;
          break;
        case 'any_update':
          shouldExecute = true;
          break;
        default:
          break;
      }

      if (!shouldExecute) continue;

      // Execute action
      const workspaceId = await getUserWorkspace(userId);
      const taskRef = doc(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}/lists/${listId}/items/${taskId}`);

      switch (rule.action) {
        case 'set_status':
          if (updates.estado !== rule.actionValue) {
            await updateDoc(taskRef, { estado: rule.actionValue, updatedAt: serverTimestamp() });
          }
          break;
        case 'set_priority':
          await updateDoc(taskRef, { prioridad: rule.actionValue, updatedAt: serverTimestamp() });
          break;
        case 'assign_to':
          await updateDoc(taskRef, { asignados: [rule.actionValue], updatedAt: serverTimestamp() });
          break;
        case 'move_section':
          await updateDoc(taskRef, { seccionId: rule.actionValue, updatedAt: serverTimestamp() });
          break;
        case 'notify':
          if (rule.actionValue) {
            await createNotification(rule.actionValue, {
              type: 'automation',
              title: 'Automatización ejecutada',
              body: `Regla "${rule.name}" aplicada a: ${taskData?.titulo || 'tarea'}`,
            });
          }
          break;
        default:
          break;
      }
    }
  } catch (error) {
    console.error('Error executing rules:', error);
  }
};

// ==================== DEPENDENCIAS ====================

export const addDependency = async (userId, projectId, folderId, listId, taskId, dependency) => {
  // dependency: { taskId, type: 'blocked_by' | 'blocking' }
  try {
    const workspaceId = await getUserWorkspace(userId);
    const taskRef = doc(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}/lists/${listId}/items/${taskId}`);
    await updateDoc(taskRef, {
      dependencies: arrayUnion(dependency),
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error al agregar dependencia:', error);
    throw error;
  }
};

export const removeDependency = async (userId, projectId, folderId, listId, taskId, dependency) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    const taskRef = doc(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}/lists/${listId}/items/${taskId}`);
    await updateDoc(taskRef, {
      dependencies: arrayRemove(dependency),
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error al eliminar dependencia:', error);
    throw error;
  }
};

// ==================== SUBTAREAS ====================

// Función auxiliar para encontrar y actualizar subtarea recursivamente
const findAndUpdateSubtask = (subtareas, path, updateFn) => {
  if (!subtareas || subtareas.length === 0) return subtareas;

  if (path.length === 0) {
    return updateFn(subtareas);
  }

  const [currentId, ...restPath] = path;

  return subtareas.map(st => {
    if (st.id === currentId) {
      if (restPath.length === 0) {
        // Encontramos la subtarea objetivo
        return updateFn(st);
      } else {
        // Continuar buscando en las subtareas anidadas
        return {
          ...st,
          subtareas: findAndUpdateSubtask(st.subtareas || [], restPath, updateFn)
        };
      }
    }
    return st;
  });
};

// Función auxiliar para obtener la ruta completa de una subtarea (breadcrumbs)
const getSubtaskPath = (subtareas, targetId, currentPath = []) => {
  if (!subtareas) return null;

  for (const subtask of subtareas) {
    const newPath = [...currentPath, { id: subtask.id, titulo: subtask.titulo }];

    if (subtask.id === targetId) {
      return newPath;
    }

    if (subtask.subtareas && subtask.subtareas.length > 0) {
      const found = getSubtaskPath(subtask.subtareas, targetId, newPath);
      if (found) return found;
    }
  }

  return null;
};

// Función auxiliar para contar subtareas recursivamente
const countSubtasks = (subtareas) => {
  if (!subtareas || subtareas.length === 0) return 0;

  let count = subtareas.length;
  subtareas.forEach(st => {
    if (st.subtareas) {
      count += countSubtasks(st.subtareas);
    }
  });

  return count;
};

export const addSubtask = async (userId, projectId, folderId, listId, taskId, subtaskData, parentPath = []) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    const taskRef = doc(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}/lists/${listId}/items/${taskId}`);
    const subtaskId = `subtask-${Date.now()}`;

    const newSubtask = {
      id: subtaskId,
      titulo: subtaskData.titulo,
      descripcion: subtaskData.descripcion || '',
      completado: false,
      estado: subtaskData.estado || 'planificacion',
      prioridad: subtaskData.prioridad || 'media',
      asignados: subtaskData.asignados || [],
      fechaVencimiento: subtaskData.fechaVencimiento || null,
      subtareas: [],
      fechaCreacion: new Date().toISOString()
    };

    if (parentPath.length === 0) {
      // Agregar al nivel raíz
      await updateDoc(taskRef, {
        subtareas: arrayUnion(newSubtask),
        updatedAt: serverTimestamp()
      });
    } else {
      // Agregar a una subtarea anidada
      const taskSnapshot = await getDoc(taskRef);
      const task = taskSnapshot.data();

      const updatedSubtareas = findAndUpdateSubtask(task.subtareas || [], parentPath, (parent) => {
        if (Array.isArray(parent)) {
          return [...parent, newSubtask];
        } else {
          return {
            ...parent,
            subtareas: [...(parent.subtareas || []), newSubtask]
          };
        }
      });

      await updateDoc(taskRef, {
        subtareas: updatedSubtareas,
        updatedAt: serverTimestamp()
      });
    }

    // Recalcular allAsignados para incluir asignados de la nueva subtarea
    await recalculateAllAsignados(taskRef);

    return subtaskId;
  } catch (error) {
    console.error('Error al agregar subtarea:', error);
    throw error;
  }
};

export const updateSubtask = async (userId, projectId, folderId, listId, taskId, subtaskPath, updates) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    const taskRef = doc(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}/lists/${listId}/items/${taskId}`);
    const taskSnapshot = await getDoc(taskRef);
    const task = taskSnapshot.data();

    const updatedSubtareas = findAndUpdateSubtask(task.subtareas || [], subtaskPath, (st) => ({
      ...st,
      ...updates
    }));

    await updateDoc(taskRef, {
      subtareas: updatedSubtareas,
      workspaceId,
      updatedAt: serverTimestamp()
    });

    // Recalcular allAsignados si cambiaron los asignados de la subtarea
    if (updates.asignados) {
      await recalculateAllAsignados(taskRef);
    }
  } catch (error) {
    console.error('Error al actualizar subtarea:', error);
    throw error;
  }
};

export const deleteSubtask = async (userId, projectId, folderId, listId, taskId, subtaskPath) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    const taskRef = doc(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}/lists/${listId}/items/${taskId}`);
    const taskSnapshot = await getDoc(taskRef);
    const task = taskSnapshot.data();

    if (subtaskPath.length === 1) {
      // Eliminar del nivel raíz
      const updatedSubtareas = task.subtareas.filter(st => st.id !== subtaskPath[0]);
      await updateDoc(taskRef, {
        subtareas: updatedSubtareas,
        updatedAt: serverTimestamp()
      });
    } else {
      // Eliminar de una subtarea anidada
      const parentPath = subtaskPath.slice(0, -1);
      const targetId = subtaskPath[subtaskPath.length - 1];

      const updatedSubtareas = findAndUpdateSubtask(task.subtareas || [], parentPath, (parent) => ({
        ...parent,
        subtareas: parent.subtareas.filter(st => st.id !== targetId)
      }));

      await updateDoc(taskRef, {
        subtareas: updatedSubtareas,
        updatedAt: serverTimestamp()
      });
    }

    // Recalcular allAsignados tras eliminar subtarea
    await recalculateAllAsignados(taskRef);
  } catch (error) {
    console.error('Error al eliminar subtarea:', error);
    throw error;
  }
};

// Exportar funciones auxiliares para uso en componentes
export { getSubtaskPath, countSubtasks };

// ==================== COMENTARIOS ====================

export const addComment = async (userId, projectId, folderId, listId, taskId, commentData) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    const taskRef = doc(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}/lists/${listId}/items/${taskId}`);

    await updateDoc(taskRef, {
      comentarios: arrayUnion({
        id: `comment-${Date.now()}`,
        usuarioId: userId,
        mensaje: commentData.mensaje,
        fechaCreacion: new Date().toISOString()
      }),
      updatedAt: serverTimestamp()
    });

    // Notify mentioned users (@email) and task assignees
    const taskSnap = await getDoc(taskRef);
    const taskData = taskSnap.data();
    const mentions = (commentData.mensaje || '').match(/@[\w.+-]+@[\w.-]+/g) || [];
    const mentionedEmails = mentions.map(m => m.slice(1));
    const assignees = taskData?.asignados || [];
    const allToNotify = [...new Set([...mentionedEmails, ...assignees])];
    if (allToNotify.length > 0) {
      notifyAssignees(allToNotify, userId, {
        type: mentionedEmails.length > 0 ? 'mention' : 'comment',
        title: mentionedEmails.length > 0 ? 'Te mencionaron en un comentario' : 'Nuevo comentario en tu tarea',
        body: `${taskData?.titulo || 'Tarea'}: ${commentData.mensaje?.slice(0, 100)}`,
      }).catch(() => {});
    }
  } catch (error) {
    console.error('Error al agregar comentario:', error);
    throw error;
  }
};

// ==================== ADJUNTOS ====================

export const addAttachment = async (userId, projectId, folderId, listId, taskId, attachmentData) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    const taskRef = doc(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}/lists/${listId}/items/${taskId}`);

    await updateDoc(taskRef, {
      adjuntos: arrayUnion({
        id: `attachment-${Date.now()}`,
        nombre: attachmentData.nombre,
        url: attachmentData.url,
        tamaño: attachmentData.tamaño,
        tipo: attachmentData.tipo,
        creadoEn: serverTimestamp()
      }),
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error al agregar adjunto:', error);
    throw error;
  }
};

export const deleteAttachment = async (userId, projectId, folderId, listId, taskId, attachmentId) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    const taskRef = doc(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}/lists/${listId}/items/${taskId}`);
    const taskSnapshot = await getDoc(taskRef);
    const task = taskSnapshot.data();

    const updatedAdjuntos = task.adjuntos.filter(att => att.id !== attachmentId);

    await updateDoc(taskRef, {
      adjuntos: updatedAdjuntos,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error al eliminar adjunto:', error);
    throw error;
  }
};

// ==================== MIEMBROS E INVITACIONES ====================

export const inviteUserByEmail = async (userId, email) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    const membersRef = doc(db, `${TASKS_BASE}/${workspaceId}/project-members/${email}`);

    await setDoc(membersRef, {
      email,
      estado: 'pendiente',
      invitadoPor: userId,
      fechaInvitacion: serverTimestamp()
    }, { merge: true });
  } catch (error) {
    console.error('Error al invitar usuario:', error);
    throw error;
  }
};

export const acceptInvitation = async (userId, inviterId) => {
  try {
    const workspaceId = await getUserWorkspace(inviterId);
    const memberRef = doc(db, `${TASKS_BASE}/${workspaceId}/members/${userId}`);

    await updateDoc(memberRef, {
      estado: 'aceptado',
      fechaAceptacion: serverTimestamp()
    });
  } catch (error) {
    console.error('Error al aceptar invitación:', error);
    throw error;
  }
};

// ==================== LISTENERS REAL-TIME ====================

export const subscribeToProjects = async (userId, callback) => {
  const workspaceId = await getUserWorkspace(userId);
  const projectsRef = collection(db, `${TASKS_BASE}/${workspaceId}/projects`);

  return onSnapshot(projectsRef, (snapshot) => {
    const projects = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    callback(projects);
  }, error => {
    console.error('Error en suscripción de proyectos:', error);
  });
};

export const subscribeToTasks = async (userId, projectId, folderId, listId, callback) => {
  const workspaceId = await getUserWorkspace(userId);
  const tasksRef = collection(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}/lists/${listId}/items`);

  return onSnapshot(tasksRef, (snapshot) => {
    trackRead('items (list)', snapshot.size, 'snapshot');
    const tasks = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    callback(tasks);
  }, error => {
    console.error('Error en suscripción de tareas:', error);
  });
};

// Suscribirse a TODAS las tareas asignadas al usuario (cross-project)
// Usa dual-query: busca en asignados (compat) y allAsignados (incluye subtareas)
// workspaceId filtra para no escanear items de otros workspaces (optimización lecturas)
export const subscribeToMyTasks = (userEmail, workspaceId, callback) => {
  // Query 1: Tareas donde el usuario está directamente asignado
  const q1 = workspaceId
    ? query(
        collectionGroup(db, 'items'),
        where('workspaceId', '==', workspaceId),
        where('asignados', 'array-contains', userEmail)
      )
    : query(
        collectionGroup(db, 'items'),
        where('asignados', 'array-contains', userEmail)
      );

  // Query 2: Tareas donde el usuario está asignado a alguna subtarea
  const q2 = workspaceId
    ? query(
        collectionGroup(db, 'items'),
        where('workspaceId', '==', workspaceId),
        where('allAsignados', 'array-contains', userEmail)
      )
    : query(
        collectionGroup(db, 'items'),
        where('allAsignados', 'array-contains', userEmail)
      );

  let results1 = [];
  let results2 = [];
  let q2Active = true;

  const mergeAndCallback = () => {
    const merged = new Map();
    results1.forEach(t => merged.set(t.id, t));
    results2.forEach(t => merged.set(t.id, t));
    callback([...merged.values()]);
  };

  const unsub1 = onSnapshot(q1, (snapshot) => {
    trackRead('items (myTasks-q1)', snapshot.size, 'snapshot');
    results1 = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    mergeAndCallback();
  }, error => {
    console.error('Error en suscripción de mis tareas (asignados):', error);
    callback([]);
  });

  // La query de allAsignados puede fallar si el índice no existe aún
  const unsub2 = onSnapshot(q2, (snapshot) => {
    trackRead('items (myTasks-q2)', snapshot.size, 'snapshot');
    results2 = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    mergeAndCallback();
  }, error => {
    console.warn('Índice allAsignados no disponible aún. Crea el índice desde Firebase Console:', error.message);
    q2Active = false;
    // No llamar callback([]) - ya tenemos results1
  });

  return () => {
    unsub1();
    if (q2Active) unsub2();
  };
};

export const subscribeToUsers = (callback) => {
  const usersRef = collection(db, 'users');
  return onSnapshot(query(usersRef, limit(100)), (snapshot) => {
    trackRead('users', snapshot.size, 'snapshot');
    const users = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    callback(users);
  }, error => {
    console.error('Error en suscripción de usuarios:', error);
  });
};

// ==================== ARCHIVO MENSUAL DE CURSOS ====================

// Get list archives (monthly snapshots)
export const getListArchives = async (userId, projectId, folderId, listId) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    const archivesRef = collection(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}/lists/${listId}/archives`);
    const snapshot = await getDocs(query(archivesRef, orderBy('archivedAt', 'desc')));
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error al obtener archivos:', error);
    return [];
  }
};

// Subscribe to list archives
export const subscribeToListArchives = (userId, projectId, folderId, listId, callback) => {
  let cancelled = false;
  let unsubscribe = null;
  getUserWorkspace(userId).then(workspaceId => {
    if (cancelled) return;
    const archivesRef = collection(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}/lists/${listId}/archives`);
    unsubscribe = onSnapshot(query(archivesRef, orderBy('archivedAt', 'desc'), limit(50)), (snapshot) => {
      callback(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
  });
  return () => { cancelled = true; if (unsubscribe) unsubscribe(); };
};

// Archive a single course (task) for a given period and reset its subtasks
export const archiveCourseAndReset = async (userId, projectId, folderId, listId, task, period) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    const listPath = `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}/lists/${listId}`;

    const subs = task.subtareas || [];
    const completed = subs.filter(s => s.estado === 'finalizado').length;
    const progress = subs.length > 0 ? Math.round(completed / subs.length * 100) : 0;

    const courseSnapshot = {
      taskId: task.id,
      titulo: task.titulo,
      subtareas: subs.map(s => ({
        id: s.id,
        titulo: s.titulo,
        estado: s.estado || 'planificacion',
        asignados: s.asignados || [],
      })),
      totalSubtasks: subs.length,
      completedSubtasks: completed,
      progress,
    };

    // Archive stored under the task item as subcollection
    const archiveId = `archive-${period.year}-${String(period.month).padStart(2, '0')}`;
    const archiveRef = doc(db, `${listPath}/items/${task.id}/archives/${archiveId}`);

    await setDoc(archiveRef, {
      id: archiveId,
      period,
      course: courseSnapshot,
      stats: {
        totalTasks: subs.length,
        completedTasks: completed,
        completionRate: progress,
      },
      archivedAt: serverTimestamp(),
      archivedBy: userId,
    });

    // Reset this task's subtareas to planificacion
    const taskRef = doc(db, `${listPath}/items/${task.id}`);
    const resetSubs = subs.map(s => ({
      ...s,
      estado: 'planificacion',
    }));
    await updateDoc(taskRef, {
      subtareas: resetSubs,
      estado: 'planificacion',
      updatedAt: serverTimestamp(),
    });

    return { archiveId, progress };
  } catch (error) {
    console.error('Error al archivar curso:', error);
    throw error;
  }
};

// Get archives for a specific course/task
export const getCourseArchives = async (userId, projectId, folderId, listId, taskId) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    const archivesPath = `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}/lists/${listId}/items/${taskId}/archives`;
    const q = query(collection(db, archivesPath), orderBy('archivedAt', 'desc'));
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error('Error al obtener archivos del curso:', error);
    return [];
  }
};

// ==================== FORMULARIOS DE SOLICITUD ====================

export const createRequestForm = async (userId, projectId, folderId, listId, formData) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    const formId = `form-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const formRef = doc(db, `${TASKS_BASE}/${workspaceId}/forms/${formId}`);
    await setDoc(formRef, {
      id: formId,
      name: formData.name || 'Formulario de solicitud',
      description: formData.description || '',
      fields: formData.fields || [
        { id: 'titulo', label: 'Título', type: 'text', required: true },
        { id: 'descripcion', label: 'Descripción', type: 'textarea', required: false },
        { id: 'prioridad', label: 'Prioridad', type: 'select', options: ['baja', 'media', 'alta'], required: false },
      ],
      targetProjectId: projectId,
      targetFolderId: folderId,
      targetListId: listId,
      active: true,
      createdBy: userId,
      workspaceId,
      createdAt: serverTimestamp(),
    });
    return formId;
  } catch (error) {
    console.error('Error creating request form:', error);
    throw error;
  }
};

export const getRequestForms = async (userId) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    const snapshot = await getDocs(collection(db, `${TASKS_BASE}/${workspaceId}/forms`));
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error('Error getting forms:', error);
    return [];
  }
};

export const getRequestFormPublic = async (formId) => {
  // Public access - search across all workspaces using collectionGroup
  try {
    const formsGroup = collectionGroup(db, 'forms');
    const q = query(formsGroup, where('id', '==', formId), where('active', '==', true));
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
  } catch (error) {
    console.error('Error getting public form:', error);
    return null;
  }
};

export const submitRequestForm = async (formId, formValues) => {
  // Public submission - creates task in target list
  try {
    const form = await getRequestFormPublic(formId);
    if (!form) throw new Error('Formulario no encontrado o inactivo');

    const { targetProjectId, targetFolderId, targetListId, workspaceId } = form;
    const taskId = `task-${Date.now()}`;
    const taskRef = doc(db, `${TASKS_BASE}/${workspaceId}/projects/${targetProjectId}/folders/${targetFolderId}/lists/${targetListId}/items/${taskId}`);

    await setDoc(taskRef, {
      id: taskId,
      listId: targetListId,
      folderId: targetFolderId,
      projectId: targetProjectId,
      workspaceId,
      titulo: formValues.titulo || 'Solicitud sin título',
      descripcion: formValues.descripcion || '',
      estado: 'planificacion',
      prioridad: formValues.prioridad || 'media',
      asignados: [],
      allAsignados: [],
      etiquetas: ['formulario'],
      fechaVencimiento: null,
      subtareas: [],
      comentarios: [],
      adjuntos: [],
      creadoPor: 'form-submission',
      fromForm: formId,
      formSubmitter: formValues._submitterName || formValues._submitterEmail || 'Anónimo',
      customFields: formValues,
      fechaCreacion: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    return taskId;
  } catch (error) {
    console.error('Error submitting form:', error);
    throw error;
  }
};

export const deleteRequestForm = async (userId, formId) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    await deleteDoc(doc(db, `${TASKS_BASE}/${workspaceId}/forms/${formId}`));
  } catch (error) {
    console.error('Error deleting form:', error);
    throw error;
  }
};

export const toggleRequestForm = async (userId, formId, active) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    await updateDoc(doc(db, `${TASKS_BASE}/${workspaceId}/forms/${formId}`), { active });
  } catch (error) {
    console.error('Error toggling form:', error);
    throw error;
  }
};

// ==================== PERMISOS POR PROYECTO ====================

export const getProjectRole = async (userId, projectId) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    const projectRef = doc(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}`);
    const snap = await getDoc(projectRef);
    if (!snap.exists()) return null;
    const data = snap.data();
    // Creator is always admin
    if (data.creadoPor === userId) return 'admin';
    // Check memberRoles map
    const roles = data.memberRoles || {};
    return roles[userId] || 'editor'; // Default to editor if member
  } catch (error) {
    console.error('Error getting project role:', error);
    return null;
  }
};

export const setProjectMemberRole = async (userId, projectId, targetUserId, role) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    const projectRef = doc(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}`);
    await updateDoc(projectRef, {
      [`memberRoles.${targetUserId}`]: role,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error setting member role:', error);
    throw error;
  }
};

export const getProjectMembers = async (userId, projectId) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    const projectRef = doc(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}`);
    const snap = await getDoc(projectRef);
    if (!snap.exists()) return [];
    const data = snap.data();
    const memberIds = data.miembros || [];
    const roles = data.memberRoles || {};
    return memberIds.map(id => ({
      userId: id,
      role: data.creadoPor === id ? 'admin' : (roles[id] || 'editor'),
      isOwner: data.creadoPor === id,
    }));
  } catch (error) {
    console.error('Error getting project members:', error);
    return [];
  }
};

// Permission check utility
export const PERMISSIONS = {
  admin: ['create', 'edit', 'delete', 'manage_members', 'manage_settings', 'view'],
  editor: ['create', 'edit', 'view'],
  viewer: ['view'],
};

export const hasPermission = (role, action) => {
  if (!role || !PERMISSIONS[role]) return false;
  return PERMISSIONS[role].includes(action);
};

export const logActivity = async (userId, projectId, folderId, listId, taskId, action, details) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    const taskRef = doc(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}/lists/${listId}/items/${taskId}`);
    await updateDoc(taskRef, {
      actividad: arrayUnion({
        id: `activity-${Date.now()}`,
        usuarioId: userId,
        accion: action,
        detalles: details,
        fecha: new Date().toISOString()
      }),
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error al registrar actividad:', error);
  }
};

// ==================== SECCIONES DE LISTAS ====================

export const getSectionsForList = async (userId, projectId, folderId, listId) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    const sectionsRef = collection(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}/lists/${listId}/sections`);
    const snapshot = await getDocs(query(sectionsRef, orderBy('orden', 'asc')));
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error al obtener secciones:', error);
    return [];
  }
};

export const createSection = async (userId, projectId, folderId, listId, sectionData) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    const sectionId = `section-${Date.now()}`;
    const sectionRef = doc(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}/lists/${listId}/sections/${sectionId}`);

    await setDoc(sectionRef, {
      id: sectionId,
      nombre: sectionData.nombre,
      color: sectionData.color || '#6b7280',
      orden: sectionData.orden || Date.now(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    return sectionId;
  } catch (error) {
    console.error('Error al crear sección:', error);
    throw error;
  }
};

export const updateSection = async (userId, projectId, folderId, listId, sectionId, updates) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    const sectionRef = doc(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}/lists/${listId}/sections/${sectionId}`);
    await updateDoc(sectionRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error al actualizar sección:', error);
    throw error;
  }
};

export const deleteSection = async (userId, projectId, folderId, listId, sectionId) => {
  try {
    const workspaceId = await getUserWorkspace(userId);
    const sectionRef = doc(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}/lists/${listId}/sections/${sectionId}`);
    await deleteDoc(sectionRef);
  } catch (error) {
    console.error('Error al eliminar sección:', error);
    throw error;
  }
};

export const createDefaultSections = async (userId, projectId, folderId, listId) => {
  try {
    const defaultSections = [
      { nombre: 'PLANIFICACIÓN', color: '#dc2626', orden: 1 },
      { nombre: 'EN CURSO', color: '#2563eb', orden: 2 },
      { nombre: 'FINALIZADA', color: '#16a34a', orden: 3 }
    ];

    for (const section of defaultSections) {
      await createSection(userId, projectId, folderId, listId, section);
    }
  } catch (error) {
    console.error('Error al crear secciones por defecto:', error);
    throw error;
  }
};

export const subscribeToSectionsForList = async (userId, projectId, folderId, listId, callback) => {
  const workspaceId = await getUserWorkspace(userId);
  const sectionsRef = collection(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}/lists/${listId}/sections`);

  return onSnapshot(query(sectionsRef, orderBy('orden', 'asc')), (snapshot) => {
    const sections = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    callback(sections);
  }, error => {
    console.error('Error en suscripción de secciones:', error);
  });
};

// ==================== VIEWS / PESTAÑAS ====================

const DEFAULT_VIEWS = [
  { name: 'Lista', type: 'lista', orden: 0 },
  { name: 'Tablero', type: 'tablero', orden: 1 },
  { name: 'Gantt', type: 'gantt', orden: 2 },
  { name: 'Panel', type: 'panel', orden: 3 },
  { name: 'Calendario', type: 'calendario', orden: 4 },
  { name: 'Carga', type: 'carga', orden: 5 },
  { name: 'Semanal', type: 'semanal', orden: 6 },
  { name: 'Cursos', type: 'cursos', orden: 7 },
];

export const subscribeToViewsForList = async (userId, projectId, folderId, listId, callback) => {
  const workspaceId = await getUserWorkspace(userId);
  const viewsRef = collection(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}/lists/${listId}/views`);
  return onSnapshot(query(viewsRef, orderBy('orden', 'asc')), (snapshot) => {
    callback(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
  }, () => callback([]));
};

export const createView = async (userId, projectId, folderId, listId, viewData) => {
  const workspaceId = await getUserWorkspace(userId);
  const viewId = `view-${Date.now()}`;
  const viewsPath = `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}/lists/${listId}/views`;
  await setDoc(doc(db, viewsPath, viewId), {
    id: viewId, ...viewData,
    filters: viewData.filters || {},
    orden: viewData.orden ?? Date.now(),
    createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
  });
  return viewId;
};

export const updateView = async (userId, projectId, folderId, listId, viewId, updates) => {
  const workspaceId = await getUserWorkspace(userId);
  const viewPath = `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}/lists/${listId}/views/${viewId}`;
  await updateDoc(doc(db, viewPath), { ...updates, updatedAt: serverTimestamp() });
};

export const deleteView = async (userId, projectId, folderId, listId, viewId) => {
  const workspaceId = await getUserWorkspace(userId);
  const viewPath = `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}/lists/${listId}/views/${viewId}`;
  await deleteDoc(doc(db, viewPath));
};

export const duplicateView = async (userId, projectId, folderId, listId, sourceView) => {
  return createView(userId, projectId, folderId, listId, {
    name: `${sourceView.name} (copia)`,
    type: sourceView.type,
    filters: sourceView.filters || {},
    orden: Date.now(),
    createdBy: userId,
  });
};

export const createDefaultViews = async (userId, projectId, folderId, listId) => {
  const workspaceId = await getUserWorkspace(userId);
  const batch = writeBatch(db);
  const viewsPath = `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}/lists/${listId}/views`;
  DEFAULT_VIEWS.forEach(v => {
    const viewId = `view-${v.type}-${Date.now() + v.orden}`;
    batch.set(doc(db, viewsPath, viewId), {
      id: viewId, name: v.name, type: v.type, filters: {},
      orden: v.orden, createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
    });
  });
  await batch.commit();
};

export const getUserDefaultView = async (userId, listId) => {
  try {
    const snap = await getDoc(doc(db, `users/${userId}/viewPreferences/${listId}`));
    return snap.exists() ? snap.data().defaultViewId : null;
  } catch { return null; }
};

export const setUserDefaultView = async (userId, listId, viewId) => {
  await setDoc(doc(db, `users/${userId}/viewPreferences/${listId}`), { defaultViewId: viewId }, { merge: true });
};

// ==================== NOTES / NOTAS ====================

export const subscribeToNotes = async (userId, projectId, folderId, listId, callback) => {
  const workspaceId = await getUserWorkspace(userId);
  const notesRef = collection(db, `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}/lists/${listId}/notes`);
  return onSnapshot(query(notesRef, orderBy('updatedAt', 'desc'), limit(50)), (snapshot) => {
    callback(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
  }, () => callback([]));
};

export const createNote = async (userId, projectId, folderId, listId, noteData) => {
  const workspaceId = await getUserWorkspace(userId);
  const noteId = `note-${Date.now()}`;
  const notesPath = `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}/lists/${listId}/notes`;
  await setDoc(doc(db, notesPath, noteId), {
    id: noteId,
    title: noteData.title || '',
    content: noteData.content || '',
    createdBy: userId,
    lastEditedBy: userId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return noteId;
};

export const updateNote = async (userId, projectId, folderId, listId, noteId, content, title) => {
  const workspaceId = await getUserWorkspace(userId);
  const notePath = `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}/lists/${listId}/notes/${noteId}`;
  const updates = { content, lastEditedBy: userId, updatedAt: serverTimestamp() };
  if (title !== undefined) updates.title = title;
  await updateDoc(doc(db, notePath), updates);
};

export const deleteNote = async (userId, projectId, folderId, listId, noteId) => {
  const workspaceId = await getUserWorkspace(userId);
  const notePath = `${TASKS_BASE}/${workspaceId}/projects/${projectId}/folders/${folderId}/lists/${listId}/notes/${noteId}`;
  await deleteDoc(doc(db, notePath));
};

// ==================== GOALS / METAS ====================

export const subscribeToGoals = (userId, callback) => {
  const goalsRef = collection(db, `users/${userId}/goals`);
  return onSnapshot(query(goalsRef, orderBy('createdAt', 'desc'), limit(50)), (snapshot) => {
    const goals = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    callback(goals);
  }, error => {
    console.error('Error subscribing to goals:', error);
  });
};

export const createGoal = async (userId, goalData) => {
  const goalsRef = collection(db, `users/${userId}/goals`);
  const docRef = await addDoc(goalsRef, {
    ...goalData,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return docRef.id;
};

export const updateGoal = async (userId, goalId, updates) => {
  const goalRef = doc(db, `users/${userId}/goals/${goalId}`);
  await updateDoc(goalRef, { ...updates, updatedAt: serverTimestamp() });
};

export const deleteGoal = async (userId, goalId) => {
  const goalRef = doc(db, `users/${userId}/goals/${goalId}`);
  await deleteDoc(goalRef);
};
